/**
 * TypeScript API Client for KYC Main Service
 * Based on actual backend API endpoints
 */

import { isAxiosError } from 'axios';
import type { AxiosInstance } from 'axios';

import { getApiBaseUrl } from '../config/api';
import { getAuthToken } from '../services/authTokenStore';
import { backendHealth } from '../utils/backendHealth';
import performanceMonitor from '../utils/performanceMonitor';
import { clearAllAuthStorage } from '../utils/tokenStore';

import { createHttpClient, attachAuthRefreshInterceptor } from './http';
// Import the ApiError class as a value
import { ApiError } from './types';
// Import interfaces as types
import type {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  AdminUser,
  AdminUserListResponse,
  AdminUserStatsResponse,
  AdminBulkUpdateResponse,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserStatusUpdateRequest,
  AdminBulkStatusUpdateRequest,
  Role,
  KYCVerification,
  Camera,
  CameraLocation,
  CameraListResponse,
  CameraStatsResponse,
  CameraCreateRequest,
  CameraUpdateRequest,
  CameraStatusUpdateRequest,
  CameraLocationDetail,
  CameraLocationUpdateRequest,
  CameraConfigurationUpdateRequest,
  CameraBulkStatusUpdateRequest,
  CameraBulkUpdateResponse,
  CameraCredentialsRolloutRequest,
  CameraCredentialsRolloutResponse,
  SiteListResponse,
  DiscoveredDevice,
  DiscoveredDeviceListResponse,
  NVRChannelInfo,
  NVRChannelListResponse,
  TrainingBatch,
  TrainingParameters,
  TrainingProgress,
  TrainingMetrics,
  TrainingBatchListResponse,
  TrainingBatchCreateRequest,
  TrainingBatchUpdateRequest,
  TrainingJobTriggerRequest,
  TrainingJobResponse,
  TrainingStatistics,
  SystemSettings,
  GeneralSettings,
  SecuritySettings,
  KYCSettings,
  NotificationSettings,
  PerformanceSettings,
  DeviceSettings,
  SettingsValidationResponse,
  SettingsAuditLog,
  SettingsAuditLogResponse,
  DashboardStats,
  SystemOverview,
  ServiceStatus,
  DatabaseStatus,
  StorageStatus,
  NetworkStatus,
  SystemHealth,
  HealthComponent,
  HealthAlert,
  UserAnalyticsFilters,
  UserAnalytics,
  UserGrowthData,
  UserActivityData,
  GeographicData,
  DeviceData,
  ActivityMetricsFilters,
  ActivityMetrics,
  ActivityTypeData,
  HourlyActivityData,
  TopUserData,
  RegistrationTrends,
  RegistrationData,
  KYCAnalyticsFilters,
  KYCAnalytics,
  ProcessingTimeData,
  DocumentTypeData,
  DailyKYCData,
  KYCStatusDistribution,
  ProcessingTimeFilters,
  ProcessingTimeMetrics,
  ProcessingTimeTrend,
  PerformanceFilters,
  SystemPerformance,
  ResponseTimeMetrics,
  ThroughputMetrics,
  ErrorRateMetrics,
  ResourceUsageMetrics,
  PerformanceTrend,
  ErrorTrackingFilters,
  ErrorTracking,
  ErrorTypeData,
  ErrorTrendData,
  TopErrorData,
  ServiceErrorData,
  UsageFilters,
  SystemUsage,
  APIUsageData,
  UserUsageData,
  ServiceUsageData,
  UsageTrendData,
  CameraAnalyticsFilters,
  CameraAnalytics,
  CameraEventTypeData,
  ZoneDistributionData,
  CameraPerformanceData,
  CameraEventTrendData,
  CameraStatusOverview,
  CameraEventFilters,
  CameraEventMetrics,
  EventTypeCount,
  EventTrendData,
  TopCameraData,
  EventSeverityData,
  ApiClientConfig,
  OTAPackage,
  OTACampaign,
  CampaignAgentStatus,
  CampaignStatus,
} from './types';

// Export the class as a value
// Mirrors main-service handler/rbac_admin_handler.go PermissionResponse.
interface RBACPermissionResponse {
  id: string;
  name: string;
  description: string;
  scope: string;
  created_at: string;
}

// Mirrors handler/rbac_admin_handler.go UserRoleResponse (optional for the
// nullable site/zone columns).
interface RBACUserRoleResponse {
  id: string;
  user_id: string;
  role_id: string;
  site_id?: string;
  zone_id?: string;
  created_at: string;
  updated_at: string;
}

// One row of GET /api/v1/admin/settings — the same { key, value, group }
// shape the bulk-update endpoints accept.
interface SettingsEntry {
  key: string;
  value: unknown;
  group?: string;
}

type WindowWithErrorLogging = Window & {
  errorLoggingService?: {
    logAPIError: (error: unknown, context: string, endpoint: string) => void;
  };
};

export { ApiError };
// Export interfaces as types to satisfy isolatedModules
export type {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  AdminUser,
  AdminUserListResponse,
  AdminUserStatsResponse,
  AdminBulkUpdateResponse,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserStatusUpdateRequest,
  AdminBulkStatusUpdateRequest,
  Role,
  KYCVerification,
  Camera,
  CameraLocation,
  CameraListResponse,
  CameraStatsResponse,
  CameraCreateRequest,
  CameraUpdateRequest,
  CameraStatusUpdateRequest,
  CameraLocationDetail,
  CameraLocationUpdateRequest,
  CameraConfigurationUpdateRequest,
  CameraBulkStatusUpdateRequest,
  CameraBulkUpdateResponse,
  CameraCredentialsRolloutRequest,
  CameraCredentialsRolloutResponse,
  DiscoveredDevice,
  DiscoveredDeviceListResponse,
  NVRChannelInfo,
  NVRChannelListResponse,
  TrainingBatch,
  TrainingParameters,
  TrainingProgress,
  TrainingMetrics,
  TrainingBatchListResponse,
  TrainingBatchCreateRequest,
  TrainingBatchUpdateRequest,
  TrainingJobTriggerRequest,
  TrainingJobResponse,
  TrainingStatistics,
  SystemSettings,
  GeneralSettings,
  SecuritySettings,
  KYCSettings,
  NotificationSettings,
  PerformanceSettings,
  SettingsValidationResponse,
  SettingsAuditLog,
  SettingsAuditLogResponse,
  DashboardStats,
  SystemOverview,
  ServiceStatus,
  DatabaseStatus,
  StorageStatus,
  NetworkStatus,
  SystemHealth,
  HealthComponent,
  HealthAlert,
  UserAnalyticsFilters,
  UserAnalytics,
  UserGrowthData,
  UserActivityData,
  GeographicData,
  DeviceData,
  ActivityMetricsFilters,
  ActivityMetrics,
  ActivityTypeData,
  HourlyActivityData,
  TopUserData,
  RegistrationTrends,
  RegistrationData,
  KYCAnalyticsFilters,
  KYCAnalytics,
  ProcessingTimeData,
  DocumentTypeData,
  DailyKYCData,
  KYCStatusDistribution,
  ProcessingTimeFilters,
  ProcessingTimeMetrics,
  ProcessingTimeTrend,
  PerformanceFilters,
  SystemPerformance,
  ResponseTimeMetrics,
  ThroughputMetrics,
  ErrorRateMetrics,
  ResourceUsageMetrics,
  PerformanceTrend,
  ErrorTrackingFilters,
  ErrorTracking,
  ErrorTypeData,
  ErrorTrendData,
  TopErrorData,
  ServiceErrorData,
  UsageFilters,
  SystemUsage,
  APIUsageData,
  UserUsageData,
  ServiceUsageData,
  UsageTrendData,
  CameraAnalyticsFilters,
  CameraAnalytics,
  CameraEventTypeData,
  ZoneDistributionData,
  CameraPerformanceData,
  CameraEventTrendData,
  CameraStatusOverview,
  CameraEventFilters,
  CameraEventMetrics,
  EventTypeCount,
  EventTrendData,
  TopCameraData,
  EventSeverityData,
  ApiClientConfig,
  OTAPackage,
  OTACampaign,
  CampaignAgentStatus,
  CampaignStatus,
};

/**
 * @deprecated Use agentApiClient (axios-based) instead. See .sisyphus/decisions/001-api-client-stack.md
 * HTTP Client class for API communication
 */
class HttpClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;
  private token: string | null = null;
  private errorInterceptors: Array<(error: unknown) => unknown> = [];
  private axiosInstance: AxiosInstance;

  constructor(config: ApiClientConfig) {
    // Migration reminder for developers only — the module-level `apiClient`
    // singleton means this fired on every page load, in production consoles
    // too, where nobody can act on it.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[Deprecated] HttpClient is deprecated. Use agentApiClient instead.'
      );
    }
    this.baseURL = config.baseURL.replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || 30000;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...config.headers,
    };

    // Shared axios transport (strangler migration off the old fetch engine).
    // auth: false — this client builds its own headers per request via
    // buildHeaders(), which has stricter semantics than the shared request
    // interceptor (explicit setToken() token wins, and tenant/site ids are
    // UUID-validated before being sent).
    this.axiosInstance = createHttpClient({
      baseURL: this.baseURL,
      name: 'main-client',
      timeout: this.timeout,
      auth: false,
    });
    // Canonical 401 → refresh → retry-once interceptor. 'logout-redirect'
    // mode: an unrecoverable 401 here already survived a real refresh
    // attempt (refreshAuthToken() was called and produced no usable token),
    // so it is never a transient/spurious 401 — wipe stored tokens and send
    // the user to /login instead of surfacing a dead-end error with no
    // recovery path. This used to be 'reject' with a comment claiming
    // "AuthContext owns the logout/redirect flow for this client" — that was
    // never true; the only thing that would have owned it, queryClient.ts's
    // handleQueryError, is never wired into QueryClient's onError (see the
    // 2026-09-16 401-on-create-site investigation). The narrower "SECURITY
    // POLICY" redirect below (request()'s catch, /auth/verify + /auth/me
    // probes only) is unaffected and stays as an earlier, more eager
    // special case for those two endpoints.
    attachAuthRefreshInterceptor(this.axiosInstance, {
      name: 'main-client',
      onAuthFailure: 'logout-redirect',
    });

    // Setup default error interceptor
    this.setupDefaultErrorInterceptor();
  }

  /**
   * Setup default error interceptor for logging and normalization
   */
  private setupDefaultErrorInterceptor(): void {
    this.addErrorInterceptor(error => {
      // Normalize error structure
      const source: Record<string, unknown> =
        typeof error === 'object' && error !== null
          ? (error as Record<string, unknown>)
          : {};
      const normalizedError = {
        message:
          typeof source.message === 'string' && source.message
            ? source.message
            : 'Unknown error',
        status: source.status,
        statusText: source.statusText,
        data: source.details,
        timestamp: new Date().toISOString(),
      };

      // Log to error logging service if available
      const errorLoggingService =
        typeof window !== 'undefined'
          ? (window as WindowWithErrorLogging).errorLoggingService
          : undefined;
      if (errorLoggingService) {
        errorLoggingService.logAPIError(
          normalizedError,
          'API Request',
          'unknown'
        );
      }

      return normalizedError;
    });
  }

  /**
   * Add error interceptor
   */
  addErrorInterceptor(interceptor: (error: unknown) => unknown): void {
    this.errorInterceptors.push(interceptor);
  }

  /**
   * Run error interceptors
   */
  private runErrorInterceptors(error: unknown): unknown {
    let processedError = error;
    for (const interceptor of this.errorInterceptors) {
      try {
        processedError = interceptor(processedError);
      } catch (err) {
        console.error('Error in error interceptor:', err);
      }
    }
    return processedError;
  }

  /**
   * Set authentication token
   */
  setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Build headers for request
   */
  private buildHeaders(
    customHeaders?: Record<string, string>
  ): Record<string, string> {
    const headers = { ...this.defaultHeaders, ...customHeaders };

    const authToken = this.token || getAuthToken();

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add tenant and site headers for multi-tenant support
    // These are required by video module and other services
    if (!headers['X-Tenant-ID']) {
      const tenantId = localStorage.getItem('tenant_id');
      // Only set if it looks like a valid tenant ID (UUID format).
      // Never send the literal string 'default' — the backend rejects it.
      if (tenantId && tenantId.includes('-') && tenantId.length >= 36) {
        headers['X-Tenant-ID'] = tenantId;
      } else {
        const logtoOrgID = import.meta.env.VITE_LOGTO_ORGANIZATION_ID;
        const canonicalTenantID = import.meta.env.VITE_LOGTO_ORG_TENANT_UUID;
        if (
          tenantId &&
          logtoOrgID &&
          tenantId === logtoOrgID &&
          canonicalTenantID &&
          canonicalTenantID.includes('-') &&
          canonicalTenantID.length >= 36
        ) {
          headers['X-Tenant-ID'] = canonicalTenantID;
        }
      }
    }

    if (!headers['X-Site-ID']) {
      const siteId = localStorage.getItem('site_id');
      if (siteId && siteId.includes('-') && siteId.length >= 36) {
        headers['X-Site-ID'] = siteId;
      }
    }

    return headers;
  }

  /**
   * Make HTTP request with performance monitoring
   */
  public async request<T>(
    method: string,
    endpoint: string,
    data?: unknown,
    customHeaders?: Record<string, string>
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.buildHeaders(customHeaders);

    // The axios instance defaults to `Content-Type: application/json`, which
    // makes axios serialise a FormData body to JSON (every File becomes {}),
    // so uploads lost their file. Declaring multipart keeps the FormData
    // intact; axios' browser adapter then drops the header so the browser
    // adds the boundary itself.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      headers['Content-Type'] = 'multipart/form-data';
    }

    // Start performance monitoring
    const requestId = `${method}_${endpoint}_${Date.now()}`;

    try {
      performanceMonitor.startRequest(requestId, endpoint, method);
    } catch (error) {
      // Performance monitoring is optional
      console.warn('Performance monitoring not available:', error);
    }

    const sendBody =
      data && (method === 'POST' || method === 'PUT' || method === 'PATCH');

    try {
      const response = await this.axiosInstance.request({
        method,
        url,
        headers,
        data: sendBody ? data : undefined,
      });

      backendHealth.recordSuccess();

      // End performance monitoring with success
      if (performanceMonitor) {
        performanceMonitor.endRequest(requestId, true);
      }

      // Handle empty responses (204 / no body) the way the fetch engine did:
      // callers always receive an object.
      if (
        response.status === 204 ||
        response.data === '' ||
        response.data == null
      ) {
        return {} as T;
      }

      // The fetch engine's response.json() threw on non-JSON bodies. Axios
      // instead hands back the raw string (a misrouted 200 serving
      // index.html is the classic case) — surface that loudly here instead
      // of letting callers crash on an HTML string typed as T.
      if (typeof response.data === 'string') {
        throw new ApiError(
          `Invalid JSON response for ${method} ${endpoint}`,
          response.status,
          { error: 'non_json_response' }
        );
      }

      return response.data as T;
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        const response = error.response;

        if (
          response.status === 502 ||
          response.status === 503 ||
          response.status === 504
        ) {
          backendHealth.recordFailure(`HTTP ${response.status} ${endpoint}`);
        }

        // The fetch engine parsed the body with `.json().catch(() => ...)`;
        // axios leaves non-JSON bodies as strings, so apply the same fallback.
        const errorData =
          typeof response.data === 'object' && response.data !== null
            ? response.data
            : { error: 'Unknown error' };

        // Build detailed error message with status code
        let errorMessage = errorData.error || `HTTP ${response.status}`;

        if (response.status === 401) {
          errorMessage = `401 Unauthorized - ${errorMessage}`;

          // SECURITY POLICY: only /auth/* probes can force-logout. Any other
          // 401 (Logto users hitting /users/me, /version.json 404, transient
          // network blip) is surfaced as an error and the session stays.
          const requestUrl = error.config?.url || url;
          const isAuthProbe =
            requestUrl.includes('/auth/verify') ||
            requestUrl.includes('/auth/me');
          if (
            isAuthProbe &&
            typeof window !== 'undefined' &&
            !window.location.pathname.startsWith('/login')
          ) {
            console.warn(
              '[API Client] auth probe rejected token — clearing and redirecting'
            );
            clearAllAuthStorage();
            window.location.replace('/login?reason=stale_token');
          } else {
            console.warn(
              `[API Client] 401 on ${requestUrl} — surfaced as error, session kept`
            );
          }
        } else if (response.status === 403) {
          errorMessage = `403 Forbidden - ${errorMessage}`;
        } else if (response.status === 500) {
          errorMessage = `500 Internal Server Error - ${errorMessage}`;
        } else if (response.status >= 400 && response.status < 500) {
          errorMessage = `${response.status} Client Error - ${errorMessage}`;
        } else if (response.status >= 500) {
          errorMessage = `${response.status} Server Error - ${errorMessage}`;
        }

        const apiError = new ApiError(errorMessage, response.status, errorData);

        // End performance monitoring with error
        if (performanceMonitor) {
          performanceMonitor.endRequest(requestId, false, apiError);
        }

        throw apiError;
      }

      // End performance monitoring with error
      if (performanceMonitor) {
        performanceMonitor.endRequest(requestId, false, error as Error);
      }

      if (error instanceof ApiError) {
        // Already an ApiError, just re-throw
        throw error;
      }

      if (isAxiosError(error)) {
        // No response — timeout or network failure. Map to the exact error
        // shapes the fetch engine threw (callers match on these messages).
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
          backendHealth.recordFailure(`timeout ${endpoint}`);
          throw new Error('Request timeout');
        }
        backendHealth.recordFailure(`network ${endpoint}`);
        throw new Error(
          'Network Error - Cannot reach backend at ' +
            this.baseURL +
            '. Check if server is running.'
        );
      }

      if (error instanceof Error) {
        // Run error interceptors (fetch-era AbortError / 'Failed to fetch'
        // cases are unreachable now — axios reports them with isAxiosError).
        const processedError = this.runErrorInterceptors(error);
        throw processedError;
      }
      backendHealth.recordFailure(`unknown ${endpoint}`);
      const unknownError = new Error('Unknown error occurred');
      const processedError = this.runErrorInterceptors(unknownError);
      throw processedError;
    }
  }

  /**
   * GET request shortcut
   */
  public async get<T>(
    endpoint: string,
    config?: { params?: object; headers?: Record<string, string> }
  ): Promise<T> {
    const queryParams = new URLSearchParams();
    if (config?.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => {
            queryParams.append(key, String(v));
          });
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }
    const queryString = queryParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request<T>('GET', url, undefined, config?.headers);
  }

  /**
   * POST request shortcut
   */
  public async post<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    return this.request<T>('POST', endpoint, data, config?.headers);
  }

  /**
   * PUT request shortcut
   */
  public async put<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, data, config?.headers);
  }

  /**
   * PATCH request shortcut
   */
  public async patch<T>(
    endpoint: string,
    data?: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    return this.request<T>('PATCH', endpoint, data, config?.headers);
  }

  /**
   * DELETE request shortcut
   */
  public async delete<T>(
    endpoint: string,
    config?: { headers?: Record<string, string> }
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, undefined, config?.headers);
  }

  // Authentication endpoints
  async login(
    username: string,
    password: string,
    deviceInfo?: string
  ): Promise<LoginResponse> {
    return this.request<LoginResponse>('POST', '/api/v1/auth/login', {
      username,
      password,
      device_info: deviceInfo,
    });
  }

  async register(userData: {
    username: string;
    password: string;
    email: string;
    first_name: string;
    last_name: string;
  }): Promise<void> {
    return this.request<void>('POST', '/api/v1/auth/register', userData);
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('POST', '/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });
  }

  async logout(): Promise<void> {
    return this.request<void>('POST', '/api/v1/auth/logout', {
      refresh_token: this.getToken(),
    });
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>('GET', '/api/v1/users/me');
  }

  async updateCurrentUser(userData: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }): Promise<void> {
    return this.request<void>('PUT', '/api/v1/users/me', userData);
  }

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    return this.request<void>('PUT', '/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async getUserById(id: string): Promise<User> {
    return this.request<User>('GET', `/users/${id}`);
  }

  // Role endpoints
  async getRoles(): Promise<Role[]> {
    return this.request<Role[]>('GET', '/roles');
  }

  async getRole(id: string): Promise<Role> {
    return this.request<Role>('GET', `/roles/${id}`);
  }

  async createRole(roleData: {
    name: string;
    description: string;
  }): Promise<Role> {
    return this.request<Role>('POST', '/roles', roleData);
  }

  async updateRole(
    id: string,
    roleData: { name?: string; description?: string }
  ): Promise<void> {
    return this.request<void>('PUT', `/roles/${id}`, roleData);
  }

  async deleteRole(id: string): Promise<void> {
    return this.request<void>('DELETE', `/roles/${id}`);
  }

  // RBAC Admin endpoints
  async getRBACRoles(): Promise<Role[]> {
    return this.request<Role[]>('GET', '/api/v1/admin/rbac/roles');
  }

  async createRBACRole(roleData: {
    name: string;
    description: string;
  }): Promise<Role> {
    return this.request<Role>('POST', '/api/v1/admin/rbac/roles', roleData);
  }

  async getRBACRole(id: string): Promise<Role> {
    return this.request<Role>('GET', `/api/v1/admin/rbac/roles/${id}`);
  }

  async updateRBACRole(
    id: string,
    roleData: { name?: string; description?: string }
  ): Promise<void> {
    return this.request<void>(
      'PUT',
      `/api/v1/admin/rbac/roles/${id}`,
      roleData
    );
  }

  async deleteRBACRole(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/v1/admin/rbac/roles/${id}`);
  }

  async getRolePermissions(roleId: string): Promise<RBACPermissionResponse[]> {
    return this.request<RBACPermissionResponse[]>(
      'GET',
      `/api/v1/admin/rbac/roles/${roleId}/permissions`
    );
  }

  async addPermissionToRole(
    roleId: string,
    permissionId: string
  ): Promise<void> {
    return this.request<void>(
      'POST',
      `/api/v1/admin/rbac/roles/${roleId}/permissions`,
      { permission_id: permissionId }
    );
  }

  async removePermissionFromRole(
    roleId: string,
    permissionId: string
  ): Promise<void> {
    return this.request<void>(
      'DELETE',
      `/api/v1/admin/rbac/roles/${roleId}/permissions/${permissionId}`
    );
  }

  async getRoleUsers(roleId: string): Promise<RBACUserRoleResponse[]> {
    return this.request<RBACUserRoleResponse[]>(
      'GET',
      `/api/v1/admin/rbac/roles/${roleId}/users`
    );
  }

  async getPermissions(): Promise<RBACPermissionResponse[]> {
    return this.request<RBACPermissionResponse[]>(
      'GET',
      '/api/v1/admin/rbac/permissions'
    );
  }

  async createPermission(permissionData: {
    name: string;
    description: string;
  }): Promise<RBACPermissionResponse> {
    return this.request<RBACPermissionResponse>(
      'POST',
      '/api/v1/admin/rbac/permissions',
      permissionData
    );
  }

  async getPermission(id: string): Promise<RBACPermissionResponse> {
    return this.request<RBACPermissionResponse>(
      'GET',
      `/api/v1/admin/rbac/permissions/${id}`
    );
  }

  async updatePermission(
    id: string,
    permissionData: { name?: string; description?: string }
  ): Promise<void> {
    return this.request<void>(
      'PUT',
      `/api/v1/admin/rbac/permissions/${id}`,
      permissionData
    );
  }

  async deletePermission(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/v1/admin/rbac/permissions/${id}`);
  }

  async assignUserRole(userId: string, roleId: string): Promise<void> {
    return this.request<void>('POST', '/api/v1/admin/rbac/user-roles', {
      user_id: userId,
      role_id: roleId,
    });
  }

  async removeUserRole(userId: string, roleId: string): Promise<void> {
    return this.request<void>('DELETE', '/api/v1/admin/rbac/user-roles', {
      user_id: userId,
      role_id: roleId,
    });
  }

  async getUserRoles(userId: string): Promise<RBACUserRoleResponse[]> {
    return this.request<RBACUserRoleResponse[]>(
      'GET',
      `/api/v1/admin/rbac/users/${userId}/roles`
    );
  }

  // KYC endpoints
  async createKYC(userData: {
    user_id: string;
    document_type: string;
  }): Promise<void> {
    return this.request<void>('POST', '/kyc', userData);
  }

  async getKYCStatus(userId: string): Promise<{
    user_id: string;
    status: string;
    created_at: string;
    updated_at: string;
  }> {
    return this.request('GET', `/kyc/user/${userId}`);
  }

  async getKYCVerifications(): Promise<KYCVerification[]> {
    return this.request<KYCVerification[]>('GET', '/kyc/verifications');
  }

  async getKYCVerification(id: string): Promise<KYCVerification> {
    return this.request<KYCVerification>('GET', `/kyc/verifications/${id}`);
  }

  async updateKYCVerificationStatus(
    id: string,
    status: 'pending' | 'approved' | 'rejected',
    notes?: string
  ): Promise<void> {
    return this.request<void>('PATCH', `/kyc/verifications/${id}`, {
      status,
      notes,
    });
  }

  // Admin User Management endpoints
  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    role?: string;
  }): Promise<AdminUserListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.role) queryParams.append('role', params.role);

    const queryString = queryParams.toString();
    const endpoint = `/admin/users${queryString ? `?${queryString}` : ''}`;

    return this.request<AdminUserListResponse>('GET', endpoint);
  }

  async createAdminUser(userData: AdminUserCreateRequest): Promise<AdminUser> {
    return this.request<AdminUser>('POST', '/admin/users', userData);
  }

  async getAdminUser(id: string): Promise<AdminUser> {
    return this.request<AdminUser>('GET', `/admin/users/${id}`);
  }

  async updateAdminUser(
    id: string,
    userData: AdminUserUpdateRequest
  ): Promise<AdminUser> {
    return this.request<AdminUser>('PUT', `/admin/users/${id}`, userData);
  }

  async updateAdminUserStatus(
    id: string,
    statusData: AdminUserStatusUpdateRequest
  ): Promise<void> {
    return this.request<void>('PUT', `/admin/users/${id}/status`, statusData);
  }

  async deleteAdminUser(id: string): Promise<void> {
    return this.request<void>('DELETE', `/admin/users/${id}`);
  }

  async getAdminUserStats(): Promise<AdminUserStatsResponse> {
    return this.request<AdminUserStatsResponse>('GET', '/admin/users/stats');
  }

  async bulkUpdateAdminUserStatus(
    bulkData: AdminBulkStatusUpdateRequest
  ): Promise<AdminBulkUpdateResponse> {
    return this.request<AdminBulkUpdateResponse>(
      'POST',
      '/admin/users/bulk-status',
      bulkData
    );
  }

  // Camera Management endpoints
  async getDiscoveredDevices(params?: {
    tenant_id?: string;
    site_id?: string;
    limit?: number;
    offset?: number;
    include_adopted?: boolean;
  }): Promise<DiscoveredDeviceListResponse> {
    const query = new URLSearchParams();
    if (params?.tenant_id) query.append('tenant_id', params.tenant_id);
    if (params?.site_id) query.append('site_id', params.site_id);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.include_adopted) query.append('include_adopted', 'true');

    // Note: This endpoint is /api/v1/admin/cameras/discovered
    return this.request<DiscoveredDeviceListResponse>(
      'GET',
      `/api/v1/admin/cameras/discovered?${query.toString()}`
    );
  }

  async updateDiscoveredDevice(
    deviceId: string,
    updates: {
      name?: string;
      description?: string;
      location?: string;
      tags?: string[];
    }
  ): Promise<DiscoveredDevice> {
    return this.request<DiscoveredDevice>(
      'PATCH',
      `/api/v1/video/discovery/devices/${deviceId}`,
      updates
    );
  }

  // --- NVR Channel Management ---

  async getNVRChannels(deviceId: string): Promise<{
    device_id: string;
    channel_count: number;
    channels: NVRChannelInfo[];
  }> {
    return this.request('GET', `/api/v1/video/nvr/${deviceId}/channels`);
  }

  async probeNVRChannels(
    deviceId: string
  ): Promise<{ status: string; device_id: string; message: string }> {
    return this.request('POST', `/api/v1/video/nvr/${deviceId}/channels/probe`);
  }

  async probeNVRChannel(
    deviceId: string,
    channel: number
  ): Promise<{
    status: string;
    device_id: string;
    channel: string;
    message: string;
  }> {
    return this.request(
      'POST',
      `/api/v1/video/nvr/${deviceId}/channels/${channel}/probe`
    );
  }

  async checkChannelPlayback(
    deviceId: string,
    channel: number
  ): Promise<{
    device_id: string;
    channel: string;
    has_playback: boolean;
    message: string;
  }> {
    return this.request(
      'GET',
      `/api/v1/video/nvr/${deviceId}/channels/${channel}/playback-check`
    );
  }

  async adoptCamera(data: AdoptCameraRequest): Promise<CameraResult> {
    return this.request<CameraResult>(
      'POST',
      '/api/v1/admin/cameras/adopt',
      data
    );
  }

  async getCameras(params?: {
    page?: number;
    limit?: number;
    offset?: number;
    status?: string;
    zone?: string;
    search?: string;
    agent_id?: string;
    device_type?: string;
    exclude_device_type?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<CameraListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset != null)
      queryParams.append('offset', params.offset.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.zone) queryParams.append('zone', params.zone);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.agent_id) queryParams.append('agent_id', params.agent_id);
    if (params?.device_type)
      queryParams.append('device_type', params.device_type);
    if (params?.exclude_device_type)
      queryParams.append('exclude_device_type', params.exclude_device_type);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/cameras${queryString ? `?${queryString}` : ''}`;

    return this.request<CameraListResponse>('GET', endpoint);
  }

  async getCamera(id: string): Promise<Camera> {
    return this.request<Camera>('GET', `/api/v1/admin/cameras/${id}`);
  }

  async createCamera(cameraData: CameraCreateRequest): Promise<Camera> {
    return this.request<Camera>('POST', '/api/v1/admin/cameras', cameraData);
  }

  // --- Site Management ---

  /**
   * Get all sites
   */
  async getSites(params?: {
    limit?: number;
    offset?: number;
  }): Promise<SiteListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset != null)
      queryParams.append('offset', params.offset.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/sites${queryString ? `?${queryString}` : ''}`;

    return this.request<SiteListResponse>('GET', endpoint);
  }

  async updateCamera(
    id: string,
    cameraData: CameraUpdateRequest
  ): Promise<Camera> {
    return this.request<Camera>(
      'PUT',
      `/api/v1/admin/cameras/${id}`,
      cameraData
    );
  }

  async deleteCamera(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/v1/admin/cameras/${id}`);
  }

  async updateCameraStatus(
    id: string,
    statusData: CameraStatusUpdateRequest
  ): Promise<void> {
    return this.request<void>(
      'PUT',
      `/api/v1/admin/cameras/${id}/status`,
      statusData
    );
  }

  async getCameraLocation(id: string): Promise<CameraLocationDetail> {
    return this.request<CameraLocationDetail>(
      'GET',
      `/api/v1/admin/cameras/${id}/location`
    );
  }

  /**
   * Partial location update — only the fields present in the payload are
   * overwritten on the backend.
   */
  async updateCameraLocation(
    id: string,
    location: CameraLocationUpdateRequest
  ): Promise<CameraLocationDetail> {
    return this.request<CameraLocationDetail>(
      'PUT',
      `/api/v1/admin/cameras/${id}/location`,
      location
    );
  }

  /**
   * Update camera configuration. The live backend accepts only
   * `enable_face_detection` here; the GET on this path is a device-runtime
   * passthrough and is intentionally not wrapped.
   */
  async updateCameraConfiguration(
    id: string,
    configuration: CameraConfigurationUpdateRequest
  ): Promise<void> {
    return this.request<void>(
      'PUT',
      `/api/v1/admin/cameras/${id}/configuration`,
      configuration
    );
  }

  async getCameraStatistics(): Promise<CameraStatsResponse> {
    return this.request<CameraStatsResponse>(
      'GET',
      '/api/v1/admin/cameras/stats'
    );
  }

  async bulkUpdateCameraStatus(
    bulkData: CameraBulkStatusUpdateRequest
  ): Promise<CameraBulkUpdateResponse> {
    return this.request<CameraBulkUpdateResponse>(
      'POST',
      '/api/v1/admin/cameras/bulk-status',
      bulkData
    );
  }

  async bulkUpdateCameraCredentials(bulkData: {
    camera_ids: string[];
    username?: string;
    password?: string;
  }): Promise<CameraBulkUpdateResponse> {
    return this.request<CameraBulkUpdateResponse>(
      'POST',
      '/api/v1/admin/cameras/bulk-credentials',
      bulkData
    );
  }

  async getCameraHealth(
    id: string
  ): Promise<{ status: string; last_seen: string; response_time: number }> {
    return this.request('GET', `/api/v1/admin/cameras/${id}/health`);
  }

  async testCameraConnection(id: string): Promise<{
    success: boolean;
    response_time: number;
    error?: string;
    rtsp_reachable?: boolean;
    auth_checked?: boolean;
    auth_valid?: boolean;
    auth_error?: boolean;
    target?: string;
    rtsp_url?: string;
  }> {
    return this.request('POST', `/api/v1/admin/cameras/${id}/test-connection`);
  }

  async refreshCameraThumbnails(payload: { camera_ids: string[] }): Promise<{
    success: boolean;
    queued_count: number;
    failed_count: number;
    queued_ids: string[];
    failed_reasons: Record<string, string>;
    message: string;
  }> {
    return this.request(
      'POST',
      '/api/v1/video/cameras/thumbnails/refresh',
      payload
    );
  }

  // System Settings endpoints - Updated to match backend API structure
  async getSystemSettings(): Promise<SystemSettings> {
    // Get all settings and transform to grouped structure
    const response = await this.request<{
      settings: SettingsEntry[];
      total: number;
    }>('GET', '/api/v1/admin/settings');
    return this.transformSettingsToGrouped(response.settings);
  }

  async getSettingsByCategory(category: string): Promise<unknown> {
    const response = await this.request<{
      settings: SettingsEntry[];
      total: number;
    }>('GET', `/api/v1/admin/settings?group=${category}`);
    const grouped = this.transformSettingsToGrouped(response.settings);
    return (grouped as unknown as Record<string, unknown>)[category];
  }

  async updateGeneralSettings(
    settings: Partial<GeneralSettings>
  ): Promise<GeneralSettings> {
    const updates = this.transformGroupedToKeyValueUpdates('general', settings);
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
    return settings as GeneralSettings;
  }

  async updateSecuritySettings(
    settings: Partial<SecuritySettings>
  ): Promise<SecuritySettings> {
    const updates = this.transformGroupedToKeyValueUpdates(
      'security',
      settings
    );
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
    return settings as SecuritySettings;
  }

  async updateKYCSettings(
    settings: Partial<KYCSettings>
  ): Promise<KYCSettings> {
    const updates = this.transformGroupedToKeyValueUpdates('kyc', settings);
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
    return settings as KYCSettings;
  }

  async updateNotificationSettings(
    settings: Partial<NotificationSettings>
  ): Promise<NotificationSettings> {
    const updates = this.transformGroupedToKeyValueUpdates(
      'notifications',
      settings
    );
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
    return settings as NotificationSettings;
  }

  async updatePerformanceSettings(
    settings: Partial<PerformanceSettings>
  ): Promise<PerformanceSettings> {
    const updates = this.transformGroupedToKeyValueUpdates(
      'performance',
      settings
    );
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
    return settings as PerformanceSettings;
  }

  async getDeviceSettings(siteId?: string): Promise<DeviceSettings> {
    const query = siteId
      ? `/api/v1/admin/settings?group=device&site_id=${encodeURIComponent(siteId)}`
      : '/api/v1/admin/settings?group=device';
    const response = await this.request<{
      settings: SettingsEntry[];
      total: number;
    }>('GET', query);

    const settingsMap: Record<string, unknown> = {};
    (response?.settings || []).forEach(setting => {
      if (setting?.key) {
        settingsMap[setting.key] = setting.value;
      }
    });

    const coerceBool = (value: unknown, fallback: boolean) => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
      }
      return fallback;
    };

    const coerceString = (value: unknown, fallback: string) => {
      if (value === null || value === undefined) return fallback;
      return String(value);
    };

    return {
      default_username: coerceString(settingsMap.default_username, 'admin'),
      default_password: coerceString(settingsMap.default_password, ''),
      apply_to_unregistered: coerceBool(
        settingsMap.apply_to_unregistered,
        false
      ),
      auto_scan_enabled: coerceBool(settingsMap.auto_scan_enabled, false),
      scan_interval_minutes: coerceString(
        settingsMap.scan_interval_minutes,
        '60'
      ),
      default_subnet: coerceString(
        settingsMap.default_subnet,
        '192.168.0.0/24'
      ),
      thumbnail_cache_minutes: coerceString(
        settingsMap.thumbnail_cache_minutes,
        '5'
      ),
      stale_device_retention_days: coerceString(
        settingsMap.stale_device_retention_days,
        '30'
      ),
    };
  }

  async updateDeviceSettings(
    settings: Partial<DeviceSettings>,
    siteId?: string
  ): Promise<DeviceSettings> {
    const updates = Object.entries(settings).map(([key, value]) => ({
      key,
      value,
      group: 'device',
    }));

    const query = siteId
      ? `/api/v1/admin/settings/bulk?site_id=${encodeURIComponent(siteId)}`
      : '/api/v1/admin/settings/bulk';

    await this.request('PUT', query, {
      settings: updates,
    });

    return settings as DeviceSettings;
  }

  async applyDefaultCameraCredentials(
    siteId?: string
  ): Promise<CameraBulkUpdateResponse> {
    const query = siteId
      ? `/api/v1/admin/cameras/credentials/apply-defaults?site_id=${encodeURIComponent(siteId)}`
      : '/api/v1/admin/cameras/credentials/apply-defaults';

    return this.request<CameraBulkUpdateResponse>('POST', query);
  }

  async validateSettings(
    _category: string,
    _settings: unknown
  ): Promise<SettingsValidationResponse> {
    // For now, return a mock validation response since backend doesn't have this endpoint
    return {
      valid: true,
      errors: [],
    };
  }

  async resetSettings(category: string): Promise<void> {
    // Reset category settings to defaults by updating with default values
    const defaultSettings = this.getDefaultSettingsForCategory(category);
    await this.updateCategorySettings(category, defaultSettings);
  }

  async exportSettings(
    categories?: string[]
  ): Promise<Partial<SystemSettings>> {
    // For now, return all settings as export data
    const allSettings = await this.getSystemSettings();
    return categories
      ? this.filterSettingsByCategories(allSettings, categories)
      : allSettings;
  }

  async importSettings(
    settingsData: unknown,
    _options: unknown = {}
  ): Promise<{ success: boolean; imported: number }> {
    // For now, just return success since backend doesn't have import endpoint
    return {
      success: true,
      imported: Object.keys(settingsData as object).length,
    };
  }

  async getSettingsAuditLog(
    filters: {
      category?: string;
      page?: number;
      limit?: number;
      start_date?: string;
      end_date?: string;
    } = {}
  ): Promise<SettingsAuditLogResponse> {
    // For now, return empty audit log since backend doesn't have this endpoint
    return {
      logs: [],
      total: 0,
      page: filters.page || 1,
      limit: filters.limit || 10,
      has_next: false,
      has_prev: false,
    };
  }

  async testNotificationSettings(
    _type: string,
    _settings: unknown
  ): Promise<{ success: boolean; message: string }> {
    // For now, return mock success response
    return { success: true, message: `Test notification sent successfully` };
  }

  async getPerformanceMetrics(): Promise<{
    api_response_time: number;
    database_query_time: number;
    cache_hit_rate: number;
    memory_usage: number;
    cpu_usage: number;
  }> {
    // For now, return mock performance metrics
    return {
      api_response_time: 150,
      database_query_time: 25,
      cache_hit_rate: 0.85,
      memory_usage: 512,
      cpu_usage: 0.45,
    };
  }

  // Helper methods for data transformation
  private transformSettingsToGrouped(settings: unknown): SystemSettings {
    const grouped: SystemSettings = {
      general: {} as GeneralSettings,
      security: {} as SecuritySettings,
      kyc: {} as KYCSettings,
      notifications: {} as NotificationSettings,
      performance: {} as PerformanceSettings,
    };

    if (!settings) {
      console.warn('ApiClient: settings is null or undefined');
      return grouped;
    }

    // If it's an object with group keys, it's already grouped
    if (typeof settings === 'object' && !Array.isArray(settings)) {
      const record = settings as Partial<SystemSettings>;
      if (
        record.general ||
        record.security ||
        record.kyc ||
        record.notifications ||
        record.performance
      ) {
        return { ...grouped, ...record };
      }

      // If it's an object but not grouped (e.g. from getSettingsByCategory), handle it
      return { ...grouped, ...record };
    }

    if (!Array.isArray(settings)) {
      console.warn('ApiClient: settings is not an array', settings);
      return grouped;
    }

    const entries = settings as SettingsEntry[];
    entries.forEach(setting => {
      const { key, value, group } = setting;
      const groups = grouped as unknown as Record<string, unknown>;
      if (group && groups[group]) {
        (groups[group] as Record<string, unknown>)[key] = value;
      }
    });

    return grouped;
  }

  private transformGroupedToKeyValueUpdates(
    category: string,
    settings: object | null | undefined
  ): Array<{ key: string; value: unknown; group: string }> {
    if (!settings || typeof settings !== 'object') {
      return [];
    }
    return Object.entries(settings).map(([key, value]) => ({
      key: `${category}.${key}`,
      value: value,
      group: category,
    }));
  }

  private getDefaultSettingsForCategory(
    category: string
  ): Record<string, unknown> {
    const defaults: Record<string, Record<string, unknown>> = {
      general: {
        site_name: 'KYC Admin Portal',
        site_description: 'Identity Verification Management System',
        timezone: 'Asia/Bangkok',
        language: 'en',
        maintenance_mode: false,
        maintenance_message: 'System is under maintenance',
        contact_email: 'admin@example.com',
        support_email: 'support@example.com',
      },
      security: {
        password_min_length: 8,
        password_require_uppercase: true,
        password_require_lowercase: true,
        password_require_numbers: true,
        password_require_symbols: true,
        session_timeout: 60,
        max_login_attempts: 5,
        lockout_duration: 15,
        two_factor_enabled: false,
        ip_whitelist: [],
      },
      kyc: {
        document_types: ['national_id', 'passport', 'driver_license'],
        max_file_size: 10,
        allowed_file_types: ['jpg', 'jpeg', 'png', 'pdf'],
        auto_approve_threshold: 85,
        manual_review_required: true,
        retention_period: 2555,
        face_detection_enabled: true,
        liveness_detection_enabled: true,
      },
      notifications: {
        email_enabled: true,
        sms_enabled: false,
        push_enabled: true,
        email_smtp_host: 'smtp.example.com',
        email_smtp_port: 587,
        email_smtp_username: '',
        email_smtp_password: '',
        email_from_address: 'noreply@example.com',
        sms_provider: 'twilio',
        sms_api_key: '',
        notification_templates: {},
      },
      performance: {
        cache_enabled: true,
        cache_ttl: 3600,
        api_rate_limit: 1000,
        max_concurrent_uploads: 5,
        image_processing_timeout: 120,
        database_connection_pool_size: 20,
        compression_enabled: true,
      },
    };

    return defaults[category] || {};
  }

  private async updateCategorySettings(
    category: string,
    settings: object
  ): Promise<void> {
    const updates = this.transformGroupedToKeyValueUpdates(category, settings);
    await this.request('PUT', '/api/v1/admin/settings/bulk', {
      settings: updates,
    });
  }

  private filterSettingsByCategories(
    allSettings: SystemSettings,
    categories: string[]
  ): Partial<SystemSettings> {
    const filtered: Partial<SystemSettings> = {};
    if (!allSettings || !categories) return filtered;
    categories.forEach(category => {
      const value = (allSettings as unknown as Record<string, unknown>)[
        category
      ];
      if (value) {
        (filtered as unknown as Record<string, unknown>)[category] = value;
      }
    });
    return filtered;
  }

  // Analytics and Dashboard endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>(
      'GET',
      '/api/v1/admin/analytics/dashboard/stats'
    );
  }

  async getSystemOverview(): Promise<SystemOverview> {
    return this.request<SystemOverview>(
      'GET',
      '/api/v1/admin/analytics/system/overview'
    );
  }

  async getSystemHealth(): Promise<SystemHealth> {
    return this.request<SystemHealth>(
      'GET',
      '/api/v1/admin/analytics/system/health'
    );
  }

  async getUserAnalytics(
    filters: UserAnalyticsFilters = {}
  ): Promise<UserAnalytics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.role) queryParams.append('role', filters.role);
    if (filters.group_by) queryParams.append('group_by', filters.group_by);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/users${queryString ? `?${queryString}` : ''}`;

    return this.request<UserAnalytics>('GET', endpoint);
  }

  async getUserActivityMetrics(
    filters: ActivityMetricsFilters = {}
  ): Promise<ActivityMetrics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.user_id) queryParams.append('user_id', filters.user_id);
    if (filters.activity_type)
      queryParams.append('activity_type', filters.activity_type);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/users/activity${queryString ? `?${queryString}` : ''}`;

    return this.request<ActivityMetrics>('GET', endpoint);
  }

  async getRegistrationTrends(
    period: string = 'daily'
  ): Promise<RegistrationTrends> {
    return this.request<RegistrationTrends>(
      'GET',
      `/api/v1/admin/analytics/users/registration-trends?period=${period}`
    );
  }

  async getKYCAnalytics(
    filters: KYCAnalyticsFilters = {}
  ): Promise<KYCAnalytics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.document_type)
      queryParams.append('document_type', filters.document_type);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/kyc${queryString ? `?${queryString}` : ''}`;

    return this.request<KYCAnalytics>('GET', endpoint);
  }

  async getKYCStatusDistribution(): Promise<KYCStatusDistribution> {
    return this.request<KYCStatusDistribution>(
      'GET',
      '/api/v1/admin/analytics/kyc/status-distribution'
    );
  }

  async getKYCProcessingTime(
    filters: KYCAnalyticsFilters = {}
  ): Promise<ProcessingTimeMetrics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/kyc/processing-time${queryString ? `?${queryString}` : ''}`;

    return this.request<ProcessingTimeMetrics>('GET', endpoint);
  }

  async getSystemPerformance(
    filters: PerformanceFilters = {}
  ): Promise<SystemPerformance> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.service) queryParams.append('service', filters.service);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/system/performance${queryString ? `?${queryString}` : ''}`;

    return this.request<SystemPerformance>('GET', endpoint);
  }

  async getErrorTracking(
    filters: ErrorTrackingFilters = {}
  ): Promise<ErrorTracking> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.severity) queryParams.append('severity', filters.severity);
    if (filters.service) queryParams.append('service', filters.service);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/system/errors${queryString ? `?${queryString}` : ''}`;

    return this.request<ErrorTracking>('GET', endpoint);
  }

  async getSystemUsage(filters: UsageFilters = {}): Promise<SystemUsage> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.user_id) queryParams.append('user_id', filters.user_id);
    if (filters.service) queryParams.append('service', filters.service);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/system/usage${queryString ? `?${queryString}` : ''}`;

    return this.request<SystemUsage>('GET', endpoint);
  }

  async getCameraAnalytics(
    filters: CameraAnalyticsFilters = {}
  ): Promise<CameraAnalytics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.camera_id) queryParams.append('camera_id', filters.camera_id);
    if (filters.event_type)
      queryParams.append('event_type', filters.event_type);
    if (filters.zone_id) queryParams.append('zone_id', filters.zone_id);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/cameras${queryString ? `?${queryString}` : ''}`;

    return this.request<CameraAnalytics>('GET', endpoint);
  }

  async getCameraStatusOverview(): Promise<CameraStatusOverview> {
    return this.request<CameraStatusOverview>(
      'GET',
      '/api/v1/admin/analytics/cameras/status'
    );
  }

  async getCameraEventMetrics(
    filters: CameraEventFilters = {}
  ): Promise<CameraEventMetrics> {
    const queryParams = new URLSearchParams();
    if (filters.start_date)
      queryParams.append('start_date', filters.start_date);
    if (filters.end_date) queryParams.append('end_date', filters.end_date);
    if (filters.camera_id) queryParams.append('camera_id', filters.camera_id);
    if (filters.event_type)
      queryParams.append('event_type', filters.event_type);
    if (filters.severity) queryParams.append('severity', filters.severity);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/admin/analytics/cameras/events${queryString ? `?${queryString}` : ''}`;

    return this.request<CameraEventMetrics>('GET', endpoint);
  }

  async refreshAnalyticsCache(cacheKey: string): Promise<void> {
    return this.request<void>(
      'POST',
      `/api/v1/admin/analytics/cache/refresh?cache_key=${cacheKey}`
    );
  }

  async clearAnalyticsCache(pattern: string): Promise<void> {
    return this.request<void>(
      'POST',
      `/api/v1/admin/analytics/cache/clear?pattern=${pattern}`
    );
  }

  // HITL Training Management endpoints
  async getTrainingBatches(params?: {
    page?: number;
    limit?: number;
    status?: string;
    model_type?: string;
    search?: string;
  }): Promise<TrainingBatchListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.model_type) queryParams.append('model_type', params.model_type);
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/hitl/training/batches${queryString ? `?${queryString}` : ''}`;

    return this.request<TrainingBatchListResponse>('GET', endpoint);
  }

  async getTrainingBatch(id: string): Promise<TrainingBatch> {
    return this.request<TrainingBatch>(
      'GET',
      `/api/v1/hitl/training/batches/${id}`
    );
  }

  async createTrainingBatch(
    batchData: TrainingBatchCreateRequest
  ): Promise<TrainingBatch> {
    return this.request<TrainingBatch>(
      'POST',
      '/api/v1/hitl/training/batches',
      batchData
    );
  }

  async updateTrainingBatch(
    id: string,
    updateData: TrainingBatchUpdateRequest
  ): Promise<TrainingBatch> {
    return this.request<TrainingBatch>(
      'PUT',
      `/api/v1/hitl/training/batches/${id}`,
      updateData
    );
  }

  async deleteTrainingBatch(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/v1/hitl/training/batches/${id}`);
  }

  async triggerTrainingJob(
    jobData: TrainingJobTriggerRequest
  ): Promise<TrainingJobResponse> {
    return this.request<TrainingJobResponse>(
      'POST',
      '/api/v1/hitl/training/jobs',
      jobData
    );
  }

  async getTrainingJobStatus(jobId: string): Promise<TrainingJobResponse> {
    return this.request<TrainingJobResponse>(
      'GET',
      `/api/v1/hitl/training/jobs/${jobId}`
    );
  }

  async cancelTrainingJob(jobId: string): Promise<void> {
    return this.request<void>(
      'POST',
      `/api/v1/hitl/training/jobs/${jobId}/cancel`
    );
  }

  async getTrainingStatistics(): Promise<TrainingStatistics> {
    return this.request<TrainingStatistics>(
      'GET',
      '/api/v1/hitl/training/statistics'
    );
  }

  async exportTrainingResults(
    batchId: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<{ download_url: string }> {
    return this.request<{ download_url: string }>(
      'POST',
      `/api/v1/hitl/training/batches/${batchId}/export`,
      { format }
    );
  }

  async getTrainingLogs(
    batchId: string,
    params?: {
      level?: 'info' | 'warning' | 'error';
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    logs: Array<{ timestamp: string; level: string; message: string }>;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.level) queryParams.append('level', params.level);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/v1/hitl/training/batches/${batchId}/logs${queryString ? `?${queryString}` : ''}`;

    return this.request<{
      logs: Array<{ timestamp: string; level: string; message: string }>;
    }>('GET', endpoint);
  }

  // Health check
  async healthCheck(): Promise<{
    status: string;
    service: string;
    version: string;
  }> {
    return this.request('GET', '/health');
  }
}

/**
 * @deprecated Use agentApiClient (axios-based) instead. See .sisyphus/decisions/001-api-client-stack.md
 * Main API Client class
 */
export interface AdoptCameraRequest {
  device_id: string;
  site_id: string;
  zone_id?: string;
  name: string;
}

interface CameraResult {
  message: string;
  camera_id: string;
}

/**
 * @deprecated Use agentApiClient (axios-based) instead. See .sisyphus/decisions/001-api-client-stack.md
 */
export class ApiClient extends HttpClient {
  constructor(config: ApiClientConfig) {
    super(config);
  }

  async rolloutCameraCredentials(
    data: CameraCredentialsRolloutRequest
  ): Promise<CameraCredentialsRolloutResponse> {
    return this.request<CameraCredentialsRolloutResponse>(
      'POST',
      '/api/v1/admin/cameras/credentials/rollout',
      data
    );
  }

  // ── Person Registry ───────────────────────────────────────────────────────

  async listPersons(params?: {
    limit?: number;
    offset?: number;
  }): Promise<import('../types/person').Person[]> {
    const query = new URLSearchParams();
    if (params?.limit != null) query.append('limit', String(params.limit));
    if (params?.offset != null) query.append('offset', String(params.offset));
    const qs = query.toString();
    return this.request<import('../types/person').Person[]>(
      'GET',
      `/api/v1/persons${qs ? `?${qs}` : ''}`
    );
  }

  async createPerson(
    data: import('../types/person').CreatePersonRequest
  ): Promise<import('../types/person').Person> {
    return this.request<import('../types/person').Person>(
      'POST',
      '/api/v1/persons',
      data
    );
  }

  async updatePerson(
    id: string,
    data: import('../types/person').UpdatePersonRequest
  ): Promise<import('../types/person').Person> {
    return this.request<import('../types/person').Person>(
      'PUT',
      `/api/v1/persons/${id}`,
      data
    );
  }

  async deletePerson(id: string): Promise<void> {
    return this.request<void>('DELETE', `/api/v1/persons/${id}`);
  }

  async enrollFace(
    personId: string,
    imageBase64: string,
    imageFormat: string
  ): Promise<{ face_id: string; message: string }> {
    return this.request<{ face_id: string; message: string }>(
      'POST',
      `/api/v1/persons/${personId}/faces`,
      {
        image_base64: imageBase64,
        image_format: imageFormat,
      }
    );
  }

  async listPersonFaces(
    personId: string
  ): Promise<import('../types/person').FaceRecord[]> {
    const resp = await this.request<{
      faces: import('../types/person').FaceRecord[];
      count: number;
    }>('GET', `/api/v1/persons/${personId}/faces`);
    return resp.faces ?? [];
  }

  async deletePersonFace(personId: string, faceId: string): Promise<void> {
    return this.request<void>(
      'DELETE',
      `/api/v1/persons/${personId}/faces/${faceId}`
    );
  }
}

// Default configuration
const defaultConfig: ApiClientConfig = {
  baseURL: getApiBaseUrl(), // Use centralized resolver for remote-host support
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

// Create and export default API client instance
/**
 * @deprecated Use agentApiClient (axios-based) instead. See .sisyphus/decisions/001-api-client-stack.md
 */
export const apiClient = new ApiClient(defaultConfig);

// Export factory function for creating custom clients
export const createApiClient = (
  config: Partial<ApiClientConfig> = {}
): ApiClient => {
  return new ApiClient({ ...defaultConfig, ...config });
};
